import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import apiService from '../services/apiService';
import { Search, ChevronLeft, ChevronRight, ClipboardList, Plus, X, Edit2, Trash2, CheckCircle } from 'lucide-react';
import AddContributionModal from '../components/payments/AddContributionModal';
import { useAuth } from '../hooks/useAuth';

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const MONTHS_FULL = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const PAYMENT_METHODS = ['Cash','Bank Transfer','Standing Order','Direct Debit'];
const fmt = (v) => `£${Number(v).toFixed(0)}`;
const ROLE_LEVEL = { office_staff: 1, admin: 2, super_user: 3 };

function StatusCell({ cell, monthIndex, year, monthlyAmount, onClick }) {
  const now = new Date();
  const isCurrent = year === now.getFullYear() && monthIndex === now.getMonth();
  const isPast   = new Date(year, monthIndex, 1) < new Date(now.getFullYear(), now.getMonth(), 1);
  const isFuture = new Date(year, monthIndex, 1) > new Date(now.getFullYear(), now.getMonth(), 1);

  if (!cell || cell.status === 'no_subscription') {
    return (
      <td className="text-center py-2 px-2 border border-slate-100">
        <span className="text-slate-300 text-xs">—</span>
      </td>
    );
  }

  if (cell.approval_status === 'voided') {
    return (
      <td className={`text-center py-1.5 px-1 border border-slate-100 ${isCurrent ? 'ring-2 ring-inset ring-blue-400' : ''}`}>
        <button onClick={onClick}
          className="w-full rounded px-1 py-1 text-xs font-medium transition-colors hover:brightness-95 border bg-slate-100 border-slate-300 text-slate-400 line-through">
          <span className="block leading-tight">void</span>
        </button>
      </td>
    );
  }

  if (cell.approval_status === 'pending') {
    return (
      <td className={`text-center py-1.5 px-1 border border-slate-100 ${isCurrent ? 'ring-2 ring-inset ring-blue-400' : ''}`}>
        <button onClick={onClick}
          className="w-full rounded px-1 py-1 text-xs font-medium transition-colors hover:brightness-95 border bg-amber-100 border-amber-300 text-amber-800">
          <span className="block leading-tight">{fmt(cell.amount)}</span>
          <span className="block leading-tight opacity-75 text-[9px]">pend.</span>
        </button>
      </td>
    );
  }

  let bg, text, label, subLabel;

  if (isFuture && cell.status === 'pending') {
    bg = 'bg-white'; text = 'text-slate-300'; label = '–';
  } else if (cell.status === 'paid') {
    const isPartial = monthlyAmount && cell.amount < monthlyAmount - 0.01;
    if (isPartial) {
      bg = 'bg-amber-50 border-amber-200'; text = 'text-amber-700';
      label = fmt(cell.amount); subLabel = `/ ${fmt(monthlyAmount)}`;
    } else {
      bg = 'bg-green-100 border-green-300'; text = 'text-green-800';
      label = fmt(cell.amount);
    }
  } else if (cell.status === 'overdue' || (cell.status === 'pending' && (isPast || isCurrent))) {
    bg = 'bg-red-100 border-red-200'; text = 'text-red-600'; label = '-';
  } else {
    bg = 'bg-slate-50'; text = 'text-slate-400'; label = '–';
  }

  return (
    <td className={`text-center py-1.5 px-1 border border-slate-100 ${isCurrent ? 'ring-2 ring-inset ring-blue-400' : ''}`}>
      <button onClick={onClick}
        className={`w-full rounded px-1 py-1 text-xs font-medium transition-colors hover:brightness-95 border ${bg} ${text}`}>
        <span className="block leading-tight">{label}</span>
        {subLabel && <span className="block leading-tight opacity-75">{subLabel}</span>}
      </button>
    </td>
  );
}

function CellPopover({ popover, isAdmin, onClose, onAddPayment, onRefresh }) {
  const [mode, setMode] = useState('view'); // 'view' | 'edit' | 'void'
  const [voidReason, setVoidReason] = useState('');
  const [editReason, setEditReason] = useState('');
  const [editFields, setEditFields] = useState({
    amount: popover.cell?.amount ?? '',
    paid_date: popover.cell?.paid_date ? popover.cell.paid_date.slice(0, 10) : '',
    payment_method: popover.cell?.payment_method || 'Cash',
  });
  const [acting, setActing] = useState(false);
  const [error, setError] = useState('');
  const ref = useRef(null);

  const { cell, row, monthIndex, year } = popover;
  const hasRecord = !!(cell?.id);
  const isVoided = cell?.approval_status === 'voided';
  const isPendingApproval = cell?.approval_status === 'pending';

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  useEffect(() => {
    const onMouse = (e) => {
      if (ref.current && !ref.current.contains(e.target)) onClose();
    };
    document.addEventListener('mousedown', onMouse);
    return () => document.removeEventListener('mousedown', onMouse);
  }, [onClose]);

  const handleVoid = async () => {
    if (voidReason.trim().length < 5) { setError('Reason must be at least 5 characters.'); return; }
    setActing(true); setError('');
    try { await apiService.voidPayment(cell.id, voidReason.trim()); onRefresh(); onClose(); }
    catch (e) { setError(e.message || 'Failed to void payment.'); setActing(false); }
  };

  const handleConfirm = async () => {
    setActing(true); setError('');
    try { await apiService.confirmPayment(cell.id); onRefresh(); onClose(); }
    catch (e) { setError(e.message || 'Failed to approve payment.'); setActing(false); }
  };

  const handleEdit = async () => {
    if (editReason.trim().length < 5) { setError('Reason must be at least 5 characters.'); return; }
    setActing(true); setError('');
    try {
      await apiService.updatePayment(cell.id, {
        amount: parseFloat(editFields.amount),
        paidDate: editFields.paid_date,
        paymentMethod: editFields.payment_method,
        status: 'paid',
        reason: editReason.trim(),
      });
      onRefresh(); onClose();
    } catch (e) { setError(e.message || 'Failed to update payment.'); setActing(false); }
  };

  const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-GB') : '—';

  // Keep popover inside viewport
  const POPOVER_W = 320;
  const POPOVER_H = 360;
  const x = Math.min(popover.x, window.innerWidth - POPOVER_W - 12);
  const y = popover.y + POPOVER_H > window.innerHeight
    ? Math.max(8, popover.y - POPOVER_H - (popover.cellH || 30) - 8)
    : popover.y;

  return (
    <div ref={ref}
      className="fixed z-50 bg-white rounded-xl shadow-2xl border border-slate-200 overflow-hidden"
      style={{ left: x, top: y, width: POPOVER_W }}>

      {/* Header */}
      <div className="flex items-start justify-between px-4 pt-4 pb-3 border-b border-slate-100">
        <div>
          <p className="font-semibold text-slate-900 text-sm leading-tight">{row.full_name}</p>
          <p className="text-xs text-slate-500 mt-0.5">{MONTHS_FULL[monthIndex]} {year}</p>
        </div>
        <button onClick={onClose} className="text-slate-400 hover:text-slate-600 ml-2 flex-shrink-0 mt-0.5">
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Expected / Paid */}
      <div className="px-4 py-3 border-b border-slate-100 space-y-1">
        <div className="flex justify-between text-sm">
          <span className="text-slate-500">Expected:</span>
          <span className="text-slate-700 font-medium">£{row.monthly_amount.toFixed(2)}</span>
        </div>
        {hasRecord && (
          <div className="flex justify-between text-sm">
            <span className="text-slate-500">Paid:</span>
            <span className={`font-semibold ${isVoided ? 'text-slate-400 line-through' : 'text-emerald-600'}`}>
              £{cell.amount.toFixed(2)}
            </span>
          </div>
        )}
      </div>

      {/* Add Payment button */}
      <div className="px-4 pt-3">
        {isVoided && (
          <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-2">
            ⚠️ This month has a voided payment. Adding a new payment will replace it.
          </p>
        )}
        <button onClick={() => onAddPayment(row, year, monthIndex)}
          className={`w-full flex items-center justify-center gap-2 px-3 py-2 text-white text-xs font-medium rounded-lg transition-colors ${isVoided ? 'bg-amber-600 hover:bg-amber-700' : 'bg-slate-900 hover:bg-slate-700'}`}>
          <Plus className="w-3.5 h-3.5" /> {isVoided ? 'Replace Voided Payment' : 'Add Payment'}
        </button>
      </div>

      {/* Existing transaction */}
      {hasRecord ? (
        <div className="px-4 pt-3 pb-4">
          <p className="text-xs font-medium text-slate-400 uppercase tracking-wide mb-2">Transactions</p>

          {mode === 'view' && (
            <div className={`rounded-lg border px-3 py-2.5 ${isVoided ? 'bg-slate-50 border-slate-200' : isPendingApproval ? 'bg-amber-50 border-amber-200' : 'bg-green-50 border-green-200'}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-slate-800">£{cell.amount.toFixed(2)}</p>
                  <p className="text-xs text-slate-500 mt-0.5">{fmtDate(cell.paid_date)} · {cell.payment_method || '—'}</p>
                  {isVoided && cell.void_reason && (
                    <p className="text-xs text-slate-400 mt-1 italic truncate">"{cell.void_reason}"</p>
                  )}
                </div>
                <div className="flex items-center gap-0.5 flex-shrink-0">
                  {isVoided
                    ? <span className="text-xs bg-slate-200 text-slate-500 px-1.5 py-0.5 rounded font-medium">Voided</span>
                    : isPendingApproval
                      ? <span className="text-xs bg-amber-200 text-amber-700 px-1.5 py-0.5 rounded font-medium">Pending</span>
                      : null
                  }
                  {!isVoided && isAdmin && (
                    <>
                      <button onClick={() => setMode('edit')} title="Edit payment"
                        className="p-1.5 rounded hover:bg-white text-slate-400 hover:text-blue-600 transition-colors">
                        <Edit2 className="w-3.5 h-3.5" />
                      </button>
                      {isPendingApproval && (
                        <button onClick={handleConfirm} disabled={acting} title="Approve"
                          className="p-1.5 rounded hover:bg-white text-slate-400 hover:text-emerald-600 transition-colors disabled:opacity-40">
                          <CheckCircle className="w-3.5 h-3.5" />
                        </button>
                      )}
                      <button onClick={() => setMode('void')} title="Void payment"
                        className="p-1.5 rounded hover:bg-white text-slate-400 hover:text-red-600 transition-colors">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}

          {mode === 'edit' && (
            <div className="space-y-2">
              <input type="number" min="0.01" step="0.01" value={editFields.amount}
                onChange={e => setEditFields(f => ({ ...f, amount: e.target.value }))}
                placeholder="Amount (£)"
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              <input type="date" value={editFields.paid_date}
                onChange={e => setEditFields(f => ({ ...f, paid_date: e.target.value }))}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              <select value={editFields.payment_method}
                onChange={e => setEditFields(f => ({ ...f, payment_method: e.target.value }))}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500">
                {PAYMENT_METHODS.map(m => <option key={m}>{m}</option>)}
              </select>
              <textarea value={editReason} onChange={e => setEditReason(e.target.value)}
                placeholder="Reason for edit (required, min 5 chars)…" rows={2}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none" />
              <div className="flex gap-2 pt-1">
                <button onClick={() => { setMode('view'); setEditReason(''); setError(''); }}
                  className="flex-1 px-3 py-1.5 border border-slate-200 text-sm rounded-lg hover:bg-slate-50 transition-colors">
                  Cancel
                </button>
                <button onClick={handleEdit} disabled={acting || !editFields.amount || editReason.trim().length < 5}
                  className="flex-1 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-40 text-white text-sm rounded-lg transition-colors">
                  {acting ? 'Saving…' : 'Save'}
                </button>
              </div>
            </div>
          )}

          {mode === 'void' && (
            <div className="space-y-2">
              <textarea value={voidReason} onChange={e => setVoidReason(e.target.value)}
                placeholder="Reason for voiding (min 5 characters)…" rows={2}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-500 resize-none" />
              <div className="flex gap-2">
                <button onClick={() => { setMode('view'); setError(''); }}
                  className="flex-1 px-3 py-1.5 border border-slate-200 text-sm rounded-lg hover:bg-slate-50 transition-colors">
                  Cancel
                </button>
                <button onClick={handleVoid} disabled={acting || voidReason.trim().length < 5}
                  className="flex-1 px-3 py-1.5 bg-red-600 hover:bg-red-700 disabled:opacity-40 text-white text-sm rounded-lg transition-colors">
                  {acting ? 'Voiding…' : 'Void'}
                </button>
              </div>
            </div>
          )}

          {error && <p className="text-xs text-red-600 mt-2">{error}</p>}
        </div>
      ) : (
        <p className="px-4 pb-4 pt-2 text-xs text-slate-400 text-center">No payment recorded for this month</p>
      )}
    </div>
  );
}

export default function ContributionsOverviewPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = (ROLE_LEVEL[user?.role] || 0) >= ROLE_LEVEL.admin;
  const year = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState(year);
  const [grid, setGrid] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [addForCell, setAddForCell] = useState(null); // { member, month } for pre-filled modal
  const [popover, setPopover] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const tableRef = useRef(null);

  const years = Array.from({ length: 5 }, (_, i) => year - 2 + i);

  const loadGrid = useCallback(() => {
    setLoading(true);
    apiService.getPaymentsGrid(selectedYear)
      .then(setGrid)
      .catch(() => setGrid([]))
      .finally(() => setLoading(false));
  }, [selectedYear]);

  useEffect(() => { loadGrid(); }, [loadGrid, refreshKey]);

  useEffect(() => {
    if (!loading && tableRef.current) {
      const currentMonthEl = tableRef.current.querySelector('[data-current="true"]');
      if (currentMonthEl) {
        currentMonthEl.scrollIntoView({ inline: 'center', behavior: 'smooth', block: 'nearest' });
      }
    }
  }, [loading]);

  const handleCellClick = useCallback((row, monthIndex, e) => {
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    setPopover({
      row,
      monthIndex,
      cell: row.months[monthIndex + 1],
      year: selectedYear,
      x: rect.left,
      y: rect.bottom + 6,
      cellH: rect.height,
    });
  }, [selectedYear]);

  const handleAddForCell = useCallback((row, cellYear, monthIndex) => {
    setPopover(null);
    const month = `${cellYear}-${String(monthIndex + 1).padStart(2, '0')}`;
    setAddForCell({
      member: {
        id: row.member_id,
        name: row.full_name,
        membershipRef: row.membership_ref,
        contribution: row.monthly_amount,
        family: [],
      },
      month,
    });
  }, []);

  const filtered = grid.filter(row => {
    const q = search.toLowerCase();
    const matchesSearch = !q || row.full_name.toLowerCase().includes(q) || row.membership_ref.includes(q);
    if (!matchesSearch) return false;

    if (statusFilter === 'all') return true;
    const currentMonth = new Date().getMonth() + 1;
    const cell = row.months[currentMonth];
    if (statusFilter === 'paid') return cell?.status === 'paid';
    if (statusFilter === 'overdue') return cell?.status === 'overdue' || cell?.status === 'pending';
    return true;
  });

  const scrollLeft = () => tableRef.current?.scrollBy({ left: -300, behavior: 'smooth' });
  const scrollRight = () => tableRef.current?.scrollBy({ left: 300, behavior: 'smooth' });

  const currentMonth = new Date().getMonth();

  return (
    <div className="p-3 sm:p-6">
      <div className="flex items-start justify-between gap-4 mb-4 sm:mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-1">Contributions Overview</h1>
          <p className="text-slate-600 text-sm">
            Track monthly payments at a glance ·{' '}
            <span className="text-blue-600 font-medium">
              Current Month: {MONTHS[new Date().getMonth()]} {new Date().getFullYear()}
            </span>
          </p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="flex items-center gap-2 px-4 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium rounded-xl shadow-sm transition-colors flex-shrink-0"
        >
          <Plus className="w-4 h-4" />
          Add Contribution
        </button>
      </div>

      <div className="bg-white rounded-xl shadow-lg overflow-hidden">
        <div className="bg-gradient-to-r from-blue-600 to-indigo-700 text-white px-4 sm:px-6 py-4">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
            <div className="flex items-center gap-2">
              <ClipboardList className="w-5 h-5" />
              <span className="font-semibold">Payment Grid ({filtered.length} members)</span>
            </div>
            <div className="flex flex-wrap gap-2 w-full sm:w-auto">
              <div className="relative flex-1 sm:w-52">
                <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-white/70" />
                <input
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  placeholder="Search member or ref…"
                  className="w-full pl-8 pr-3 py-1.5 bg-white/20 border border-white/30 text-white placeholder-white/60 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-white/50"
                />
              </div>
              <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
                className="px-3 py-1.5 bg-white/20 border border-white/30 text-white rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-white/50">
                <option value="all" className="text-slate-800">All Status</option>
                <option value="paid" className="text-slate-800">Paid this month</option>
                <option value="overdue" className="text-slate-800">Overdue this month</option>
              </select>
              <select value={selectedYear} onChange={e => setSelectedYear(Number(e.target.value))}
                className="px-3 py-1.5 bg-white/20 border border-white/30 text-white rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-white/50">
                {years.map(y => <option key={y} value={y} className="text-slate-800">{y}</option>)}
              </select>
              <div className="flex gap-1">
                <button onClick={scrollLeft} className="p-1.5 rounded-lg bg-white/20 hover:bg-white/30 text-white transition-colors">
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <button onClick={scrollRight} className="p-1.5 rounded-lg bg-white/20 hover:bg-white/30 text-white transition-colors">
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Legend */}
        <div className="flex items-center gap-4 px-4 sm:px-6 py-2.5 border-b border-slate-100 bg-slate-50/50">
          <div className="flex items-center gap-3 text-xs text-slate-500 ml-auto flex-wrap">
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-green-100 border border-green-300 inline-block"/>Paid</span>
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-amber-50 border border-amber-200 inline-block"/>Partial</span>
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-red-100 border border-red-200 inline-block"/>Missed</span>
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-white border border-slate-200 inline-block"/>Future</span>
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-amber-100 border border-amber-300 inline-block"/>Pending</span>
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-slate-100 border border-slate-300 inline-block"/>Voided</span>
            <span className="text-slate-400 italic">· Click any cell to manage</span>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-16 text-slate-400">Loading grid…</div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 text-slate-400">No members found</div>
        ) : (
          <div ref={tableRef} className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 sticky top-0 z-10">
                <tr>
                  <th className="sticky left-0 bg-slate-50 text-left px-4 py-3 font-medium text-slate-600 min-w-52 border-r border-slate-200">
                    Member
                  </th>
                  {MONTHS.map((m, i) => (
                    <th key={m} data-current={i === currentMonth && selectedYear === year}
                      className={`text-center px-2 py-3 font-medium min-w-16 border-b border-slate-200
                        ${i === currentMonth && selectedYear === year
                          ? 'text-blue-600 bg-blue-50'
                          : 'text-slate-500'}`}>
                      {m}
                      {i === currentMonth && selectedYear === year && (
                        <div className="text-xs font-normal text-blue-500">Current</div>
                      )}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {filtered.map(row => (
                  <tr key={row.member_id} className="hover:bg-slate-50/50 transition-colors">
                    <td className="sticky left-0 bg-white px-4 py-2.5 border-r border-slate-100 min-w-52">
                      <button onClick={() => navigate(`/members/${row.member_id}`)}
                        className="text-left hover:text-blue-600 transition-colors w-full">
                        <p className="font-medium text-slate-800 text-sm leading-tight">{row.full_name}</p>
                        <p className="text-xs text-slate-400">{row.membership_ref} · {fmt(row.monthly_amount)}/mo</p>
                      </button>
                    </td>
                    {MONTHS.map((_, i) => (
                      <StatusCell
                        key={i}
                        cell={row.months[i + 1]}
                        monthIndex={i}
                        year={selectedYear}
                        monthlyAmount={row.monthly_amount}
                        onClick={(e) => handleCellClick(row, i, e)}
                      />
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Cell action popover */}
      {popover && (
        <CellPopover
          popover={popover}
          isAdmin={isAdmin}
          onClose={() => setPopover(null)}
          onAddPayment={handleAddForCell}
          onRefresh={() => setRefreshKey(k => k + 1)}
        />
      )}

      {/* Global Add Contribution modal */}
      {showAddModal && (
        <AddContributionModal
          onClose={() => setShowAddModal(false)}
          onSaved={() => { setShowAddModal(false); setRefreshKey(k => k + 1); }}
        />
      )}

      {/* Cell-specific Add Payment modal (pre-filled) */}
      {addForCell && (
        <AddContributionModal
          initialMember={addForCell.member}
          initialMonth={addForCell.month}
          onClose={() => setAddForCell(null)}
          onSaved={() => { setAddForCell(null); setRefreshKey(k => k + 1); }}
        />
      )}
    </div>
  );
}
